import { useEffect, useRef, useState, type ReactNode } from 'react'

export interface ConfirmDialogProps {
  open: boolean
  title: string
  body: ReactNode
  confirmLabel?: string
  destructive?: boolean
  busy?: boolean
  confirmPhrase?: string
  onConfirm: () => void
  onCancel: () => void
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel = 'Confirm',
  destructive = false,
  busy = false,
  confirmPhrase,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null)
  const [typed, setTyped] = useState('')

  useEffect(() => {
    const dialog = ref.current
    if (!dialog) return

    if (open && !dialog.open) dialog.showModal()
    if (!open && dialog.open) dialog.close()
  }, [open])

  const [lastOpen, setLastOpen] = useState(open)
  if (open !== lastOpen) {
    setLastOpen(open)
    setTyped('')
  }

  const unlocked = confirmPhrase === undefined || typed === confirmPhrase

  return (
    <dialog
      ref={ref}
      aria-labelledby="confirm-dialog-title"

      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onCancel()
      }}
      className="bg-surface m-auto w-full max-w-md rounded-xl border p-6 shadow-xl backdrop:bg-gray-800/40"
    >
      <h2 id="confirm-dialog-title" className="text-text mb-2 text-base font-medium">
        {title}
      </h2>
      <div className="text-text-2 mb-4 text-[12px] leading-relaxed">{body}</div>

      {confirmPhrase === undefined ? null : (
        <div className="mb-6">
          <label
            className="text-text-2 mb-1.5 block text-[11px] font-medium"
            htmlFor="confirm-phrase"
          >
            Type <strong className="text-text">{confirmPhrase}</strong> to confirm
          </label>
          <input
            id="confirm-phrase"
            className="bg-surface border-border-strong text-text w-full rounded-md border px-3 py-2 text-[12px] transition-colors outline-none focus:border-green-400"
            value={typed}
            autoComplete="off"
            onChange={(event) => {
              setTyped(event.target.value)
            }}
          />
        </div>
      )}
      <div className="canopy-save-row justify-end">
        <button type="button" className="canopy-btn" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={destructive ? 'canopy-btn canopy-btn-danger' : 'canopy-btn canopy-btn-primary'}
          onClick={onConfirm}
          disabled={busy || !unlocked}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </dialog>
  )
}
